// components/AddToCartButton.jsx - улучшенная версия
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useUserStore from '../store/userStore';
import useCartStore from '../store/cartStore';
import { prepareCheckoutEvent } from '../utils/analytics';
import { singleItemCheckout } from '../utils/singleItemCheckout';

const AddToCartButton = ({
  isInCart: externalIsInCart,
  slug,
  price,
  collection,
  media_type,
  onAddToCart,
  className,
}) => {
  // Состояние для отслеживания, отрендерилась ли кнопка в правильном состоянии
  const [initialRenderComplete, setInitialRenderComplete] = useState(false);
  
  // Подписываемся напрямую на состояние корзины и получаем список товаров
  const cartItems = useCartStore(state => state.cartItems);
  const totalPrice = useCartStore(state => state.totalPrice);
  
  // Проверяем, находится ли текущий товар в корзине
  const isInCartFromStore = React.useMemo(() => {
    return cartItems.some(item => item.slug === slug && item.collection === collection);
  }, [cartItems, slug, collection]);
  
  // Используем комбинированное состояние - как из пропсов, так и из стора
  const isActuallyInCart = initialRenderComplete ? isInCartFromStore : externalIsInCart;
  
  // Локальное состояние для отслеживания, в корзине ли товар
  const [localIsInCart, setLocalIsInCart] = useState(externalIsInCart);
  
  // Флаг для определения, нужно ли анимировать переход
  const [shouldAnimate, setShouldAnimate] = useState(false);
  
  // Состояния загрузки
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Состояние ошибки
  const [error, setError] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  
  // Состояния для hover эффектов
  const [isAddButtonHovered, setIsAddButtonHovered] = useState(false);
  const [isCheckoutButtonHovered, setIsCheckoutButtonHovered] = useState(false);
  
  // Инициализация при первом рендере
  useEffect(() => {
    // Первоначально устанавливаем состояние без анимации
    setLocalIsInCart(externalIsInCart);
    
    // Отложенно разрешаем рендер, чтобы все стили применились корректно
    const timer = setTimeout(() => {
      setInitialRenderComplete(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Синхронизируем с актуальным состоянием корзины 
  useEffect(() => {
    if (initialRenderComplete) {
      // Не включаем анимацию при синхронизации с хранилищем
      if (localIsInCart !== isActuallyInCart) {
        setLocalIsInCart(isActuallyInCart);
      }
    }
  }, [isActuallyInCart, initialRenderComplete]);
  
  // Сбрасываем флаг анимации после завершения перехода
  useEffect(() => {
    if (shouldAnimate) {
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 600); // Длительность анимации
      
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate]);
  
  // Сбрасываем ошибку через таймаут
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  
  // Сбрасываем ошибку чекаута через таймаут
  useEffect(() => {
    if (checkoutError) {
      const timer = setTimeout(() => {
        setCheckoutError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [checkoutError]);
  
  // Обработчик добавления в корзину - ТОЛЬКО здесь включаем анимацию
  const handleAddToCart = async () => {
    if (isLoading || localIsInCart) return;
    
    setIsLoading(true);
    setError(false);
    
    // ВАЖНО: Только здесь анимируем переход и обновляем локальное состояние
    setShouldAnimate(true);
    setLocalIsInCart(true);
    
    try {
      // Для визуальной обратной связи ждем некоторое время
      const animationPromise = new Promise(resolve => setTimeout(resolve, 300));
      
      // Вызываем функцию onAddToCart параллельно с анимацией
      const [, apiResult] = await Promise.allSettled([
        animationPromise,
        onAddToCart ? onAddToCart() : Promise.resolve(true)
      ]);
      
      if (apiResult.status === 'rejected') {
        console.error('Background error in onAddToCart:', apiResult.reason);
        setError(true);
        // Если ошибка, возвращаем исходное состояние
        setLocalIsInCart(false);
      } else {
        // Успешное добавление в корзину, отправляем событие add_to_cart в GA4
        if (typeof window !== 'undefined' && window.dataLayer) {
          const eventData = {
            event: 'add_to_cart',
            assetSlug: slug,        // Для вашей настройки GTM
            item_id: slug,          // Стандартное имя в GA4
            assetName: slug,        // Для вашей настройки GTM
            item_name: slug,        // Стандартное имя в GA4
            assetPrice: price,      // Для вашей настройки GTM
            price: price            // Стандартное имя в GA4
          };
          
          window.dataLayer.push(eventData);
        }
      }
    } catch (error) {
      console.error('Error in handleAddToCart:', error);
      setError(true);
      setLocalIsInCart(false);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }
  };

  // Обработчик перехода к оформлению заказа, использующий новую утилиту
  const handleCheckout = async () => {
    if (checkoutLoading) return;
    
    console.log("[BUTTON] Starting checkout for single item:", { slug, collection, price });
    setCheckoutLoading(true);
    setCheckoutError(null);
    
    try {
      // Создаем объект товара для чекаута
      const item = {
        assetId: `${collection}-${slug}`,
        slug,
        collection,
        mediaType: media_type,
        product: {
          price: price
        }
      };
      
      // Используем новую утилиту для оформления заказа одиночного товара
      // Обратите внимание, мы передаем готовый объект item
      const result = await singleItemCheckout({
        item,
        price,
        slug,
        collection,
        mediaType: media_type,
        setLoading: setCheckoutLoading,
        setError: setCheckoutError
      });
      
      // В норме результат не обрабатываем, так как происходит редирект
      // Обрабатываем только случай когда функция вернулась без редиректа
      if (!result.success && !result.redirected) {
        console.error("[BUTTON] Checkout failed but no redirect happened:", result.error);
      }
    } catch (error) {
      console.error('[BUTTON] Error in handleCheckout:', error);
      setCheckoutError("An error occurred during checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  // ДОБАВЛЕН МЕТОД ДЛЯ ЭКСПОРТА: создание статического плейсхолдера
  AddToCartButton.renderPlaceholder = (isCartItem, itemPrice) => {
    return (
      <div className="relative min-h-[55px] overflow-hidden w-full">
        {/* Плейсхолдер "Добавить в корзину" */}
        <button
          className="absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-10 font-bold text-center"
          style={{ 
            backgroundColor: '#303233',
            transform: isCartItem ? 'translateX(calc(100% + 10px))' : 'translateX(0)', 
            opacity: isCartItem ? 0 : 1 
          }}
          disabled={true}
        >
          <div className="flex items-center justify-center w-full h-full px-4">
            <span className="inline-block">{`Add SVG to Cart $${typeof itemPrice === 'number' ? itemPrice.toFixed(2) : '0.00'}`}</span>
          </div>
        </button>
        
        {/* Плейсхолдер "Перейти к оформлению" */}
        <button
          className="absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-[1] font-bold text-center"
          style={{ 
            backgroundColor: '#303233',
            transform: isCartItem ? 'translateX(0)' : 'translateX(calc(-100% - 10px))', 
            opacity: isCartItem ? 1 : 0 
          }}
          disabled={true}
        >
          <div className="flex items-center justify-center w-full h-full px-4">
            <span className="inline-block">{"Proceed to Checkout"}</span>
          </div>
        </button>
      </div>
    );
  };

  // Не показываем ничего до завершения начальной настройки
  if (!initialRenderComplete) {
    return (
      <div className={`relative min-h-[55px] overflow-hidden ${className || ''}`}>
        {/* Статичный плейсхолдер на время загрузки компонента */}
        <button
          className="absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-10 font-bold text-center"
          style={{ 
            backgroundColor: '#303233',
            transform: localIsInCart ? 'translateX(calc(100% + 10px))' : 'translateX(0)', 
            opacity: localIsInCart ? 0 : 1 
          }}
          disabled={true}
        >
          <div className="flex items-center justify-center w-full h-full px-4">
            <span className="inline-block">{`Add SVG to Cart $${price.toFixed(2)}`}</span>
          </div>
        </button>
        
        {/* Плейсхолдер для кнопки "Перейти к оформлению" */}
        <button
          className="absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-[1] font-bold text-center"
          style={{ 
            backgroundColor: '#303233',
            transform: localIsInCart ? 'translateX(0)' : 'translateX(calc(-100% - 10px))', 
            opacity: localIsInCart ? 1 : 0 
          }}
          disabled={true}
        >
          <div className="flex items-center justify-center w-full h-full px-4">
            <span className="inline-block">{"Proceed to Checkout"}</span>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className={`relative min-h-[55px] overflow-hidden ${className || ''}`}>
      {/* Отображение ошибки чекаута, если есть */}
      {checkoutError && (
        <div className="absolute top-[-40px] left-0 right-0 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
          {checkoutError}
        </div>
      )}
      
      {/* Кнопка "Добавить в корзину" */}
      <button
        className="absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-10 font-bold text-center"
        style={{ 
          backgroundColor: isAddButtonHovered ? '#000000' : '#303233',
          transform: localIsInCart ? 'translateX(calc(100% + 10px))' : 'translateX(0)', 
          opacity: localIsInCart ? 0 : 1,
          transition: shouldAnimate ? 'transform 500ms ease-in-out, opacity 500ms ease-in-out, background-color 200ms ease' : 'background-color 200ms ease', 
        }}
        onClick={handleAddToCart}
        onMouseEnter={() => setIsAddButtonHovered(true)}
        onMouseLeave={() => setIsAddButtonHovered(false)}
        disabled={isLoading || localIsInCart}
      >
        <div className="flex items-center justify-center w-full h-full px-4">
          <span className="inline-block">{`Add SVG to Cart $${price.toFixed(2)}`}</span>
          
          {isLoading && (
            <span className="ml-4 inline-block">
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 100 100" 
                preserveAspectRatio="xMidYMid"
              >
                <circle cx="50" cy="50" fill="none" stroke="#fff" strokeWidth="8" r="35" strokeDasharray="164.93361431346415 56.97787143782138">
                  <animateTransform 
                    attributeName="transform" 
                    type="rotate" 
                    repeatCount="indefinite" 
                    dur="1s" 
                    values="0 50 50;360 50 50" 
                    keyTimes="0;1"
                  />
                </circle>
              </svg>
            </span>
          )}
        </div>
      </button>

      {/* Кнопка "Перейти к оформлению" */}
      <button
        className="absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-[1] font-bold text-center"
        style={{ 
          backgroundColor: isCheckoutButtonHovered ? '#000000' : '#303233',
          transform: localIsInCart ? 'translateX(0)' : 'translateX(calc(-100% - 10px))', 
          opacity: localIsInCart ? 1 : 0,
          transition: shouldAnimate ? 'transform 500ms ease-in-out, opacity 500ms ease-in-out, background-color 200ms ease' : 'background-color 200ms ease',
        }}
        onClick={handleCheckout}
        onMouseEnter={() => setIsCheckoutButtonHovered(true)}
        onMouseLeave={() => setIsCheckoutButtonHovered(false)}
        disabled={checkoutLoading || !localIsInCart}
      >
        <div className="flex items-center justify-center w-full h-full px-4">
          <span className="inline-block">{"Proceed to Checkout"}</span>
          {checkoutLoading && (
            <span className="ml-4 inline-block">
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 100 100" 
                preserveAspectRatio="xMidYMid"
              >
                <circle cx="50" cy="50" fill="none" stroke="#fff" strokeWidth="8" r="35" strokeDasharray="164.93361431346415 56.97787143782138">
                  <animateTransform 
                    attributeName="transform" 
                    type="rotate" 
                    repeatCount="indefinite" 
                    dur="1s" 
                    values="0 50 50;360 50 50" 
                    keyTimes="0;1"
                  />
                </circle>
              </svg>
            </span>
          )}
        </div>
      </button>
    </div>
  );
};

AddToCartButton.propTypes = {
  isInCart: PropTypes.bool.isRequired,
  slug: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  collection: PropTypes.string.isRequired,
  media_type: PropTypes.string.isRequired,
  onAddToCart: PropTypes.func,
  className: PropTypes.string,
}; 

export default AddToCartButton;